import React from 'react';
import Link from 'next/link.js';
import type { Cell, Row } from 'react-table';
import { shortenHash } from '@soundxyz/utils/const';
import { styled } from '@components-shared/src/stitches';
import { Avatar } from '@components/basics/Images/Avatar';
import { Cover } from '@components/basics/Images/Cover';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import { webappUriPath } from '@utils/url';
import type { ArtistRow, CollectorRow, CuratorRow, ReleaseRow } from './types';

function TrendingTableTd({
  cell,
  cellIdx,
}: {
  cell: Cell<ReleaseRow | CollectorRow | ArtistRow | CuratorRow>;
  cellIdx: number;
}) {
  const { key, ...cellProps } = cell.getCellProps();

  return (
    <Td key={key} {...cellProps} type="numerical">
      {cellIdx > 0 && (
        <>
          {cell.column.id !== 'releaseInfo' && cell.column.id !== 'address' && cell.render('Cell')}
        </>
      )}
    </Td>
  );
}

export function TrendingTableRow({
  row,
  rowIdx,
  isLaptop,
  selectedColumn,
  loading,
}: {
  row: Row<ReleaseRow | CollectorRow | ArtistRow | CuratorRow>;
  rowIdx: number;
  isTablet: boolean;
  isLaptop: boolean;
  selectedColumn: string;
  loading?: boolean;
}) {
  const avatarSize = isLaptop ? 53 : 30;

  return (
    <Tr>
      {row.cells.map((cell, cellIdx) => {
        const { key, ...cellProps } = cell.getCellProps();

        if (
          !isLaptop &&
          cell.column.Header !== selectedColumn &&
          ['totalSales', 'totalSpent', 'totalVolume'].includes(cell.column.id)
        ) {
          return null;
        }

        if (cell.column.id === 'totalSales') {
          const cellValue: ArtistRow['totalSales'] | ReleaseRow['totalSales'] = cell.value;
          return (
            <Td key={key} {...cellProps}>
              {cellIdx > 0 && <>{`${cellValue.eth} ETH`}</>}
            </Td>
          );
        }
        if (cell.column.id === 'totalSpent') {
          const cellValue: CollectorRow['totalSpent'] = cell.value;
          return (
            <Td key={key} {...cellProps} type="numerical">
              {cellIdx > 0 && <>{`${cellValue.eth} ETH`}</>}
            </Td>
          );
        }

        if (cell.column.id === 'totalVolume') {
          const cellValue: CuratorRow['totalVolume'] = cell.value;
          return (
            <Td key={key} {...cellProps} type="numerical">
              {cellIdx > 0 && <>{`${cellValue} ETH`}</>}
            </Td>
          );
        }

        if (cell.column.id === 'releaseInfo') {
          const cellValue: ReleaseRow['releaseInfo'] = cell.value;

          return (
            <Td key={key} {...cellProps}>
              <Flex>
                {loading ? (
                  <CoverSkeleton size={avatarSize} />
                ) : (
                  <Link href={webappUriPath(cellValue.webappUri)} passHref>
                    <Cover
                      src={cellValue.coverUrl}
                      size={avatarSize}
                      alt={cellValue.title}
                      borderRadius={5}
                    />
                  </Link>
                )}

                <SongInfo>
                  <ArtistOrReleaseName>
                    <Link href={webappUriPath(cellValue.webappUri)} passHref>
                      <UserButton>{cellValue.title}</UserButton>
                    </Link>
                  </ArtistOrReleaseName>
                  <Link href={webappUriPath(cellValue.artistInfo.webappUri)} passHref>
                    <SubTextArtistHandle>{cellValue.artistInfo.name}</SubTextArtistHandle>
                  </Link>
                </SongInfo>
              </Flex>
            </Td>
          );
        }

        if (cell.column.id === 'artistInfo') {
          const cellValue: ArtistRow['artistInfo'] = cell.value;

          return (
            <Td key={key} {...cellProps}>
              <Flex>
                <AvatarWrapper>
                  {loading ? (
                    <CoverSkeleton size={avatarSize} />
                  ) : (
                    <Link href={webappUriPath(cellValue.webappUri)} passHref>
                      <Avatar
                        src={cellValue.avatarUrl}
                        size={avatarSize}
                        username={cellValue.username}
                        walletAddress={cellValue.address}
                        borderRadius={5}
                        disableLink
                        webappUri={cellValue.webappUri}
                      />
                    </Link>
                  )}
                </AvatarWrapper>

                <SongInfo>
                  <ArtistOrReleaseName>
                    <Link href={webappUriPath(cellValue.webappUri)} passHref>
                      <UserButton>{cellValue.name}</UserButton>
                    </Link>
                  </ArtistOrReleaseName>
                  <Link href={webappUriPath(cellValue.webappUri)} passHref>
                    <SubTextArtistHandle>{`@${
                      cellValue.twitterHandle
                        ? cellValue.twitterHandle
                        : cellValue.soundHandle || '...'
                    }`}</SubTextArtistHandle>
                  </Link>
                </SongInfo>
              </Flex>
            </Td>
          );
        }

        if (cell.column.id === 'rank')
          return (
            <Td key={key} type="textual">
              {'#' + (rowIdx + 1)}
            </Td>
          );

        if (cell.column.id === 'userInfo') {
          const cellValue: CollectorRow['userInfo'] = cell.value;
          const shortenedPublicAddressHash = shortenHash(cellValue.address, 4, 4);
          const userNameIsPublicAddress =
            shortenHash(cellValue.username, 4, 4) === shortenedPublicAddressHash;

          return (
            <Td key={key}>
              <Flex>
                <AvatarWrapper>
                  {loading ? (
                    <CoverSkeleton size={avatarSize} />
                  ) : (
                    <Avatar
                      src={cellValue.avatarUrl}
                      size={avatarSize}
                      username={cellValue.address}
                      walletAddress={cellValue.address}
                      borderRadius="5px"
                      webappUri={cellValue.webappUri}
                    />
                  )}
                </AvatarWrapper>

                <CollectorContainer>
                  <BigUserName>
                    <Link href={webappUriPath(cellValue.webappUri)} passHref>
                      <UserButton>
                        {userNameIsPublicAddress ? shortenedPublicAddressHash : cellValue.username}
                      </UserButton>
                    </Link>
                  </BigUserName>
                  {!userNameIsPublicAddress && (
                    <Link href={webappUriPath(cellValue.webappUri)} passHref>
                      <GreyBox>{shortenedPublicAddressHash}</GreyBox>
                    </Link>
                  )}
                </CollectorContainer>
              </Flex>
            </Td>
          );
        }

        return !isLaptop ? (
          cell.column.Header === selectedColumn ? (
            <TrendingTableTd key={key} cell={cell} cellIdx={cellIdx} />
          ) : null
        ) : (
          <TrendingTableTd key={key} cell={cell} cellIdx={cellIdx} />
        );
      })}
    </Tr>
  );
}

const Flex = styled('div', {
  display: 'flex',
  alignItems: 'center',
});

const ArtistOrReleaseName = styled('h3', {
  fontSize: '0.65rem',
  lineHeight: 2,
  fontWeight: 500,
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',

  '@media (min-width: 380px)': {
    fontSize: '0.75rem',
    lineHeight: 1.4,
  },

  '@laptop': {
    fontSize: '1rem',
  },
});

const CollectorContainer = styled('div', {
  fontSize: '0.65rem',
  fontFamily: '$base',
  lineHeight: 0.5,
  marginX: '1rem',
  fontWeight: 500,

  '@media (min-width: 380px)': {
    fontSize: '0.75rem',
    lineHeight: 1,
  },

  '@laptop': {
    fontSize: '1rem',
  },
});

const BigUserName = styled('h3', {
  fontFamily: '$title',
});

const UserButton = styled('a', {
  fontFamily: '$title',
  marginBottom: '0.4rem',
  '&:hover': { color: '$grey600 ' },
  '@laptop': {
    marginBottom: '0',
  },
});

const SongInfo = styled('div', {
  marginX: '1rem',
  textAlign: 'left',
  maxWidth: 100,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',

  '@tablet': {
    maxWidth: 300,
  },
});

const SubTextArtistHandle = styled('a', {
  fontSize: '0.65rem',
  fontFamily: '$base',
  color: '$grey400',
  marginTop: 0,
  lineHeight: 0.5,
  fontWeight: 600,
  textAlign: 'left',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  maxWidth: 100,

  '&:hover': {
    color: '$grey600',
  },

  '@media (min-width: 380px)': {
    fontSize: '0.75rem',
    lineHeight: 1.4,
    maxWidth: 300,
  },

  '@laptop': {
    fontSize: '1rem',
  },

  variants: {
    noMarginTop: {
      true: {
        '@laptop': {
          marginTop: 0,
        },
      },
    },
  },
});

const Tr = styled('tr', {
  height: 50,
  '@laptop': {
    height: 80,
  },
});

const GreyBox = styled('button', {
  backgroundColor: '$grey100',
  borderRadius: '8px',
  height: '20px',
  fontSize: '.7rem',
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  marginTop: '.5rem',
  padding: '6px 9px',
  '&:hover': {
    color: '$grey600',
  },
});

const Td = styled('td', {
  fontWeight: 600,
  fontFamily: '$base',
  fontSize: '0.8rem',
  flexShrink: 1,
  fontFeatureSettings: 'tnum',
  backgroundColor: 'white',

  '&:first-child': {
    fontSize: '0.75rem ',
    color: '$grey400',
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px',
    width: '50px',
  },

  // total volume
  '&:nth-child(3)': {
    textAlign: 'right',
  },

  '&:last-child': {
    textAlign: 'right',
    borderTopRightRadius: '16px',
    borderBottomRightRadius: '16px',
  },

  '@media (min-width: 380px)': {
    fontSize: '0.95rem',
  },

  '@tablet': {
    '&:last-child': {
      width: 'auto',
    },
  },

  '@laptop': {
    fontSize: 16,
    '&:first-child': {
      fontSize: '1rem',
    },
  },

  variants: {
    type: {
      numerical: {
        textAlign: 'right',
      },
      textual: {
        textAlign: 'left',
      },
    },
    font: {
      base: {
        fontFamily: '$base',
      },
      title: {
        fontFamily: '$title',
      },
    },
  },
});

const CoverSkeleton = styled(Skeleton, {
  borderRadius: '5px',

  variants: {
    size: {
      53: {
        width: 53,
        height: 53,
      },
      30: {
        width: 30,
        height: 30,
      },
    },
  },
});

const AvatarWrapper = styled('div', {
  flexShrink: 0,
});
